import { ReactElement, useMemo } from "react";
import parse from "html-react-parser";
import { Box, VBox } from "./Box";
import { LoadingIndicator } from "./LoadingIndicator";
import { LoadingOverlay } from "./LoadingOverlay";
import { Tabs } from "./Tabs";
import { classNames } from "../utils/classNames";
import { Button, LinkButton, LinkButtonProps } from "./Button";
import { isMobileView } from "../utils/screen";
import { EmptyState } from "./EmptyState";

import { useTranslation } from "../stores/RootStoreContext";
import { DownloadAsPdfButton, DownloadAsPdfButtonProps } from "../common/commonComponents";

const formatContent = (content: string) =>
  content
    .replace(/max-width:.*?px;/, "")
    .replaceAll(
      /<div.*?page-break-before:.*?><\/div>/g,
      "<hr style='border:none; border-top: 2px dashed #aaa; margin: 50px 0'/>",
    );

function DefaultEmptyState() {
  const { t } = useTranslation();
  return (
    <VBox textAlign={"center"} fg={"subtle"} padding>
      <div className={"text-h3"}>{t("document-preview.empty-title")}</div>
      <div className={"text-lg"}>{t("document-preview.empty-description")}</div>
    </VBox>
  );
}

function DocumentView({
  content,
  download,
  padding,
  dropShadow,
  extraAction,
}: {
  content?: string;
  download?: DownloadAsPdfButtonProps;
  padding?: boolean;
  dropShadow?: boolean;
  extraAction?: LinkButtonProps;
}) {
  const parsed = useMemo(() => content && parse(formatContent(content), { trim: true }), [content]);

  return (
    <VBox paddingX={!isMobileView() && padding} grow paddingBottom>
      <VBox
        dropShadow={!isMobileView() && dropShadow}
        bg
        className={"tw-relative lg:tw-p-12 xl:tw-p-24"}
      >
        <div className={classNames("revert-all", download && "tw-pt-6")}>
          <div className={"content"}>{parsed}</div>
        </div>
        {extraAction && (
          <Box className={"tw-absolute tw-top-4 tw-left-8"}>
            <LinkButton {...extraAction} />
          </Box>
        )}
        {download && (
          <Box className={"tw-absolute tw-top-4 tw-right-8"}>
            <DownloadAsPdfButton {...download} variant={"link"} />
          </Box>
        )}
      </VBox>
    </VBox>
  );
}

export type DocumentPreviewProps = {
  content?:
    | { type: "custom"; content: ReactElement }
    | { type: "document"; documentId: string; content?: string }[];
  tabs?: {
    id: string;
    label: string;
    isActive: boolean;
    onSelect: () => void;
  }[];
  tabHeader?: ReactElement;
  isUpdating?: boolean;
  download?: (content: {
    type: "document";
    documentId: string;
    content?: string;
  }) => DownloadAsPdfButtonProps | undefined;
  centered?: boolean;
  padding?: boolean;
  marginTop?: boolean;
  emptyState?: ReactElement;
  dropShadow?: boolean;
  extraAction?: LinkButtonProps;
};

export function DocumentPreview({
  content,
  tabs,
  isUpdating,
  download,
  centered = true,
  padding = true,
  marginTop,
  emptyState,
  dropShadow = true,
  extraAction,
  tabHeader,
}: DocumentPreviewProps) {
  const { t } = useTranslation();
  const mobile = isMobileView();

  const render = (): ReactElement => {
    switch (true) {
      case tabs?.length === 0:
        return (
          <VBox grow={"golden"} justifyContent={"center"}>
            {emptyState ?? <DefaultEmptyState />}
          </VBox>
        );

      case content === undefined:
        return (
          <VBox grow={"golden"} justifyContent={"center"}>
            <LoadingIndicator />
          </VBox>
        );

      case Array.isArray(content) && content.length === 1 && !content[0].content:
        return (
          <VBox grow={"golden"} justifyContent={"center"} fg={"subtle"}>
            <EmptyState
              illustration={"file"}
              title={t("document-preview.missing-preview.title")}
              description={t("document-preview.missing-preview.description")}
              actions={
                download && (
                  <Button
                    variant={"secondary"}
                    label={t("document-preview.missing-preview.download-pdf")}
                    icon={"download"}
                    onClick={download(content[0])?.onDownload}
                  />
                )
              }
            />
          </VBox>
        );

      case Array.isArray(content):
        return (
          <VBox gap={"lg"} className={"relative"} overflow={"auto"}>
            {content.map((c) => (
              <DocumentView
                key={c.documentId}
                {...c}
                dropShadow={dropShadow}
                padding={padding}
                download={download?.(c)}
                extraAction={extraAction}
              />
            ))}
            {isUpdating && <LoadingOverlay />}
          </VBox>
        );

      default:
        return (
          <VBox
            paddingX={!mobile && padding}
            grow
            overflowY={mobile ? undefined : "auto"}
            className={"relative"}
          >
            {content.content}
          </VBox>
        );
    }
  };

  return (
    <VBox
      grow
      marginX={centered ? "auto" : undefined}
      overflowY={mobile ? undefined : "hidden"}
      className={"tw-w-full tw-max-w-[900px]"}
      marginTop={marginTop}
    >
      {tabs && tabs.length > 1 && <Tabs tabs={tabs} />}
      {tabHeader && <div className={classNames("tw-flex-none", !mobile && "px")}>{tabHeader}</div>}
      {render()}
    </VBox>
  );
}
